import { useState } from 'react';
import { api, describeError, type VideoPlanView } from './api.js';

interface Segment {
  start?: number;
  end?: number;
  part?: string;
  shot?: string;
  visual?: string;
  voiceover?: string;
  onScreenText?: { text?: string } | string | null;
}

interface Plan {
  angle?: string;
  lengthSec?: number;
  segments?: Segment[];
  productFootage?: string[];
  caption?: string;
  hashtags?: string[];
  audio?: string;
  whyThisShouldWork?: string;
}

const SHOT_LABEL: Record<string, string> = {
  presenter: 'Presenter',
  'product-footage': 'Your screen recording',
  'b-roll': 'B-roll',
  'text-card': 'Text card',
};

const secs = (n: unknown) => (typeof n === 'number' ? `${Math.round(n * 10) / 10}s` : '?');

/**
 * Video plans (blueprints): each one's script and shots, the reel it copies, and the screen
 * recordings Anthony needs to film before a batch can be made from it.
 */
export function VideoPlans({
  plans,
  projectName,
  onChanged,
  onNotice,
}: {
  plans: VideoPlanView[];
  projectName: string;
  /** Reload after a change. */
  onChanged: () => Promise<void>;
  onNotice: (text: string) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const remove = async (p: VideoPlanView) => {
    if (!window.confirm(`Remove the video plan “${p.title}”? It won’t be used for videos.`)) return;
    setBusy(p.id);
    try {
      await api.removePlan(p.project, p.id);
      onNotice(`Removed “${p.title}”.`);
      await onChanged();
    } catch (err) {
      onNotice(describeError(err));
    } finally {
      setBusy(null);
    }
  };
  if (plans.length === 0) return null;
  return (
    <section className="panel">
      <h2 className="panel-title">Video plans ({plans.length})</h2>
      <p className="muted small">
        Scripts Alfred made from winning reels. To turn them into videos: pick a presenter, upload
        the screen recordings each plan lists (Settings → Video footage), then tell Alfred “make{' '}
        {Math.min(plans.length, 7)} {plans.length === 1 ? 'video' : 'videos'} for {projectName}”.
      </p>
      <ul className="idea-list">
        {plans.map((p) => {
          const plan = (p.plan ?? {}) as Plan;
          const isOpen = open === p.id;
          return (
            <li key={p.id}>
              <div>
                <strong>{p.title}</strong>
                {plan.lengthSec ? <span className="muted small"> · {plan.lengthSec}s</span> : null}
              </div>
              {p.source && (
                <div className="inspiration small">
                  <span className="muted">Copying the format of </span>
                  <a href={p.source.url} target="_blank" rel="noreferrer">
                    {p.source.label} ↗
                  </a>
                  <span className="muted"> · {p.source.numbers}</span>
                </div>
              )}
              {plan.angle && <div className="small">{plan.angle}</div>}
              {plan.productFootage && plan.productFootage.length > 0 && (
                <div className="small plan-footage">
                  <strong>Screen recordings to film:</strong>
                  <ul>
                    {plan.productFootage.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="idea-actions">
                <button
                  className="btn btn-small"
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : p.id)}
                >
                  {isOpen ? 'Hide the script' : 'Show the script'}
                </button>
                <button
                  className="btn btn-small"
                  disabled={busy === p.id}
                  onClick={() => void remove(p)}
                >
                  {busy === p.id ? 'Removing…' : 'Remove'}
                </button>
              </div>
              {isOpen && (
                <div className="plan-script small">
                  <ol>
                    {(plan.segments ?? []).map((s, i) => {
                      const text =
                        typeof s.onScreenText === 'string' ? s.onScreenText : s.onScreenText?.text;
                      return (
                        <li key={i}>
                          <div className="muted">
                            {secs(s.start)}–{secs(s.end)} · {s.part ?? ''} ·{' '}
                            {SHOT_LABEL[s.shot ?? ''] ?? s.shot ?? ''}
                          </div>
                          {s.voiceover && <div>“{s.voiceover}”</div>}
                          {text && (
                            <div>
                              <span className="muted">On screen:</span> {text}
                            </div>
                          )}
                          {s.visual && <div className="muted">{s.visual}</div>}
                        </li>
                      );
                    })}
                  </ol>
                  {plan.caption && (
                    <p>
                      <span className="muted">Caption:</span> {plan.caption}
                      {plan.hashtags?.length
                        ? ` ${plan.hashtags.map((h) => `#${h.replace(/^#/, '')}`).join(' ')}`
                        : ''}
                    </p>
                  )}
                  {plan.audio && (
                    <p>
                      <span className="muted">Audio:</span> {plan.audio}
                    </p>
                  )}
                  {plan.whyThisShouldWork && (
                    <p>
                      <span className="muted">Why it should work:</span> {plan.whyThisShouldWork}
                    </p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
